import { useCallback, useState, memo } from 'react';
import { useStore } from '@hotjar/recordings/src/store/player/context';
import styled from '@emotion/styled';
import { useTrack } from '@hotjar/tracking';
import { useTranslation } from 'react-i18next';
import { Button, Text, tokens, Modal } from '@hotjar/hotsauce';

import { pause } from '@hotjar/recordings/src/features/Playbacks/utils';
import { SimilarRecordingsPopup } from './SimilarRecordingsPopup/SimilarRecordingsPopup';

const StyledContainer = styled.div`
  margin: 0 ${tokens.spacer.x2} ${tokens.spacer.x2};
  padding: ${tokens.spacer.x2};

  background: ${tokens.color.bg_info};
  border: solid 1px ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};

  position: relative;
`;

const StyledButton = styled(Button)`
  margin-top: ${tokens.spacer.x2};
`;

const UnMemoSimilarRecordingsSection = (): JSX.Element => {
  const { t } = useTranslation('common');
  const { click } = useTrack();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const store = useStore();

  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const onButtonClick = useCallback(() => {
    setIsModalOpen(true);
    click('View similar recordings', 'session_info', {});
    // Pause the recording when opening the modal
    pause(store);
  }, [click, store]);

  return (
    <StyledContainer>
      <Text textType="body2">
        {t('player.session-info.similar-recordings-session.description')}
      </Text>
      <StyledButton
        kind="secondary"
        size="s"
        onClick={onButtonClick}
        data-testid="player-similar-recording-section-button"
      >
        {t('player.session-info.similar-recordings-session.button')}
      </StyledButton>
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        contentLabel={t('player.session-info.similar-recordings-session.popup.title')}
        size="s"
      >
        <Modal.Header title={t('player.session-info.similar-recordings-session.popup.title')} />
        <Modal.Body>
          <SimilarRecordingsPopup onClose={closeModal} />
        </Modal.Body>
      </Modal>
    </StyledContainer>
  );
};

export const SimilarRecordingsSection = memo(UnMemoSimilarRecordingsSection);
